import { PublicClientApplication, type Configuration } from '@azure/msal-browser'

/**
 * Sign-in against Microsoft Entra ID.
 *
 * Entra rather than a username and password table of our own. The reasoning is
 * the same one recorded in Program.cs: every audit column and every activity-feed
 * entry records who acted, so the register cannot ship anonymous — and storing
 * passwords ourselves would mean owning hashing, rotation, lockout and a
 * reset flow, none of which is this project's job. To the person signing in it
 * is still a user id and a password; the difference is where the password lives.
 *
 * Values are injected at build time. A public client has no secret to protect —
 * the client id and tenant id are not credentials, they are addresses — but they
 * do differ per environment, so they must not be hardcoded.
 */

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined
const apiScope = import.meta.env.VITE_API_SCOPE as string | undefined

/**
 * Whether sign-in is configured at all.
 *
 * False in a build with no Entra values — which is how the app runs against a
 * local API using DevAuth. The UI reads this rather than crashing on a missing
 * client id, so a developer without a tenant still gets a working app.
 */
export const authConfigured = Boolean(clientId && tenantId)

/**
 * The domain appended to a bare user name on the sign-in screen.
 *
 * Injected rather than hardcoded, because it is the one part of the sign-in
 * address that changes with the tenant — and getting it wrong sends people to a
 * Microsoft page prefilled with an account that does not exist.
 */
export const signInDomain =
  (import.meta.env.VITE_SIGNIN_DOMAIN as string | undefined) ?? 'prarambhportal.online'

const configuration: Configuration = {
  auth: {
    clientId: clientId ?? '',
    authority: `https://login.microsoftonline.com/${tenantId ?? 'common'}`,
    redirectUri: window.location.origin,
  },
  cache: {
    // sessionStorage, not localStorage: the register is used on shared site
    // machines, and a token that survives the browser closing is a token the
    // next person inherits.
    cacheLocation: 'sessionStorage',
  },
}

export const msal = new PublicClientApplication(configuration)

/**
 * The scope the API expects.
 *
 * Not `openid`/`profile` — those get an ID token, which proves who someone is to
 * the front end and means nothing to the API. The API validates an ACCESS token
 * whose audience is the API's own app registration, which is what this asks for.
 */
export const apiRequest = {
  scopes: apiScope ? [apiScope] : [],
}

/**
 * A bearer token for the API, refreshed silently when possible.
 *
 * Returns null when auth is not configured, so the client falls back to sending
 * no Authorization header — correct against a DevAuth API, and correctly refused
 * by a real one.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!authConfigured) return null

  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0]
  if (!account) return null

  try {
    const result = await msal.acquireTokenSilent({ ...apiRequest, account })
    return result.accessToken
  } catch {
    // The refresh token has expired or consent was revoked. A popup rather than
    // a redirect keeps whatever the user was doing — a half-filled add-vehicle
    // form survives, where a redirect would discard it.
    try {
      const result = await msal.acquireTokenPopup({ ...apiRequest, account })
      return result.accessToken
    } catch {
      return null
    }
  }
}

/**
 * Gets a token NOW, while a click is still fresh, and says so if it cannot.
 *
 * Two problems this solves, both of which produced a bare "Request failed (401)".
 *
 * A popup opened after an `await` has lost the browser's user activation and is
 * blocked — so a flow that does work first and refreshes the token second can
 * never recover a stale session. Called at the top of a click handler, the popup
 * is still in the gesture's call stack and opens.
 *
 * And a null token is not "no auth": the request went out with no Authorization
 * header at all, and the API refused it correctly. Throwing here turns that into
 * a sentence somebody can act on.
 */
export async function requireAccessToken(): Promise<void> {
  if (!authConfigured) return

  const token = await getAccessToken()

  if (!token) {
    throw new Error(
      'Your session has expired, or the sign-in window was blocked. ' +
        'Reload the page and sign in again, then try once more.',
    )
  }
}
